"use client";

// Kampanye promo aktif untuk SELURUH storefront (mig 3470).
//
// KENAPA CONTEXT, BUKAN FETCH PER KARTU. Daftar kampanye satu toko itu kecil
// (hitungan puluhan baris) dan sama untuk semua pengunjung, sedangkan kartu
// produk dirender puluhan kali per layar — beranda 2 kolom + rail + hasil cari.
// `HomeProductCard` bahkan sengaja `prefetch={false}` demi Entry Process shared
// hosting (lihat komentar di komponennya); menambah satu fetch per kartu akan
// membatalkan penghematan itu. Jadi: ambil SEKALI, sebar lewat context, resolve
// per-produk dengan fungsi murni (`promosForProduct` / `pickCheapestPromo`).
//
// `initialCampaigns` datang dari root layout (server, `fetchActivePromosServer`)
// supaya harga promo sudah ikut di HTML pertama — tanpa itu pembeli melihat
// harga normal sekejap lalu berubah.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { useQuery } from "@tanstack/react-query";

import { useCustomerGroup } from "@/context/CustomerContext";

import { promoAllowsLevel, promoHasRemaining, promosForProduct, runningPromoCampaigns,
  type ProductPromo, type PromoCampaign, type PromoProductRef, type PromoRemainingMap,
  promosForLevel, promoBoundaryTimerMs, promoNextBoundaryMs, promoRefetchIntervalMs,
  guestPromosFor
} from "@/lib/promo";
import { fetchActivePromoCampaigns, fetchPromoRemaining } from "@/lib/supabase/promo";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";

type PromoContextValue = {
  campaigns: PromoCampaign[];
  /**
   * "Sekarang" versi klien. `null` sampai komponen ter-mount — lihat catatan
   * hidrasi di `usePromosFor`.
   */
  now: Date | null;
  /**
   * Sisa kuota kampanye (mig 6020). Kunci yang ABSEN = tak berkuota atau belum
   * terbaca — kampanyenya tetap ditawarkan (lihat `promoHasRemaining`).
   */
  remaining: PromoRemainingMap;
};

const EMPTY_REMAINING: PromoRemainingMap = new Map();

const PromoContext = createContext<PromoContextValue>({
  campaigns: [],
  now: null,
  remaining: EMPTY_REMAINING
});

export function PromoProvider({
  children,
  initialCampaigns = [],
  serverNow
}: {
  children: ReactNode;
  initialCampaigns?: PromoCampaign[];
  /**
   * Stempel waktu dari server (ISO). WAJIB dikirim root layout.
   *
   * Kenapa bukan `new Date()` saja: promo berbatas waktu, jadi HTML server dan
   * render klien PERTAMA harus memakai jam yang SAMA persis — kalau tidak, satu
   * sisi bisa berdiri di seberang `ends_at` dan React melempar hydration
   * mismatch (#418), persis alasan `HomeProductCard` menerima `now` sebagai
   * prop alih-alih membuatnya sendiri. Setelah mount, jam hidup mengambil alih.
   */
  serverNow?: string | null;
}) {
  // Satu jam dinding untuk seluruh pohon: kalau tiap kartu memanggil
  // `new Date()` sendiri, dua kartu bisa berbeda sisi dari `ends_at` dalam satu
  // render yang sama.
  const [now, setNow] = useState<Date | null>(() => {
    if (!serverNow) return null;
    const t = new Date(serverNow);
    return Number.isFinite(t.getTime()) ? t : null;
  });
  useEffect(() => {
    setNow(new Date());
    // Perbarui tiap menit supaya kampanye yang berakhir di tengah kunjungan
    // benar-benar berhenti tanpa perlu reload.
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const storeId = getClientStoreId();

  const [data, setData] = useState<PromoCampaign[] | undefined>(undefined);
  const known = data ?? initialCampaigns;
  // R9/A10: muat ulang TEPAT di batas kampanye terdekat (starts_at/ends_at),
  // bukan tiap 2 menit — di menit-menit terakhir kampanye, kartu masih memajang
  // harga promo sementara checkout (yang membaca ulang) sudah menagih normal.
  const refetchInterval = useMemo(
    () => promoRefetchIntervalMs(known, now),
    [known, now]
  );
  const { data: fetched } = useQuery({
    // storeId masuk kunci: satu browser bisa berpindah domain toko (dan cache
    // TanStack Query adalah singleton lintas-navigasi).
    queryKey: ["promo", "active", storeId],
    queryFn: fetchActivePromoCampaigns,
    // Tanpa toko ter-resolusi, data layer memang memulangkan [] (fail-closed) —
    // tak perlu menembak query sama sekali.
    enabled: Boolean(storeId),
    initialData: initialCampaigns,
    staleTime: 120_000,
    refetchInterval
  });
  useEffect(() => {
    setData(fetched);
  }, [fetched]);
  // Jam dinding ikut diputar tepat di batas (bukan menunggu detak 60 dtk):
  // begitu `ends_at` lewat, `runningPromoCampaigns` langsung menggugurkannya.
  //
  // ⚠️ TUNDAANNYA DIKLEM (`promoBoundaryTimerMs`). `setTimeout` menyimpan
  // tundaan sebagai int32: di atas 2^31−1 ms (≈24,8 hari) browser MENJALANKANNYA
  // SEGERA → `setNow` → efek terpasang ulang → loop render tanpa henti di
  // SETIAP halaman storefront toko itu (bug live zzhomey 2026-09-17: batas
  // kampanye terdekat 31 Des). Diklem ke plafon refetch, efek hanya menunggu
  // paling lama 5 menit lalu menghitung ulang sisa jaraknya.
  const nextBoundary = useMemo(() => promoNextBoundaryMs(known, now), [known, now]);
  useEffect(() => {
    const delay = promoBoundaryTimerMs(nextBoundary);
    if (delay === null) return;
    const id = window.setTimeout(() => setNow(new Date()), delay);
    return () => window.clearTimeout(id);
  }, [nextBoundary]);

  // Kuota kampanye (mig 6020). HANYA kampanye yang benar-benar berkuota yang
  // ditanyakan — toko tanpa kuota tak menembak satu RPC pun. Kuncinya daftar id
  // itu sendiri, jadi kampanye berkuota yang baru muncul langsung memicu
  // pembacaan tanpa menunggu detak berikutnya.
  const quotaIds = useMemo(
    () =>
      known
        .filter((c) => c.quotaTotal !== null)
        .map((c) => c.id)
        .sort(),
    [known]
  );
  const { data: remainingData } = useQuery({
    queryKey: ["promo", "remaining", storeId, quotaIds.join(",")],
    queryFn: () => fetchPromoRemaining(quotaIds),
    enabled: Boolean(storeId) && quotaIds.length > 0,
    // Angka sisa bergerak karena pembeli LAIN, bukan karena layar ini — 60 dtk
    // cukup untuk chip "Sisa N pcs". Penolakan sebenarnya tetap di trigger DB,
    // jadi angka yang telat sedetik tak pernah menjadi harga yang salah.
    staleTime: 60_000,
    refetchInterval: 60_000
  });

  const value = useMemo<PromoContextValue>(() => {
    const all = data ?? initialCampaigns;
    // LAPIS KEDUA (bukan pengganti filter query). Kampanye milik toko lain yang
    // entah bagaimana lolos ke sini tidak boleh dipakai menghitung harga: target
    // kategori/merek dicocokkan BY NAMA, jadi "Gamis" toko lain akan mendiskon
    // "Gamis" toko ini tanpa satu pun error. Kampanye tanpa storeId (berkas lama
    // / mapper defensif) dibiarkan lewat agar tak mematikan fitur diam-diam.
    const campaigns = storeId
      ? all.filter((c) => c.storeId == null || c.storeId === storeId)
      : all;
    return { campaigns, now, remaining: remainingData ?? EMPTY_REMAINING };
  }, [data, initialCampaigns, now, storeId, remainingData]);

  return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
}

/**
 * Seluruh kampanye aktif MENTAH — belum disaring periode MAUPUN kelayakan level.
 *
 * HAMPIR SELALU BUKAN YANG KAMU MAU. Permukaan yang memajang kampanye/produknya
 * ke pembeli WAJIB memakai `useCampaignsForMe()`; hook mentah ini hanya untuk
 * pemakai yang memang perlu daftar apa adanya (mis. pratinjau admin). Dijaga
 * tripwire `promoLevelScopeLint.test.ts`.
 */
export function usePromoCampaigns(): PromoCampaign[] {
  return useContext(PromoContext).campaigns;
}

/**
 * Kampanye yang BOLEH DILIHAT pembeli ini — berjalan sekarang DAN berhak atas
 * levelnya (mig 3680). SATU pintu untuk seluruh permukaan yang menilai kampanye
 * sendiri, bukan lewat `usePromosFor` per-produk.
 *
 * Lahir dari bug live 2026-08-19: rail "Sedang Promo" di beranda menilai
 * kampanye sendiri lewat `usePromoCampaigns` + `runningPromoCampaigns` dan LUPA
 * menyaring level, sehingga tamu melihat rail berisi produk kampanye ber-level
 * — lengkap dengan judulnya — sementara harga di kartunya tetap normal karena
 * `getVariantPrice` memang menyaring. Daftar dan harga bercerita hal berbeda di
 * satu layar. Halaman `/promo` sudah benar, tapi aturannya disalin di sana;
 * salinan kedua itulah yang tak pernah sampai ke beranda.
 *
 * `fallback` dipakai saat jam dinding belum ada (`now === null`, layout lupa
 * mengirim `serverNow`): pemanggil menyerahkan daftar yang sudah diresolusi
 * SERVER alih-alih menebak periodenya sendiri. Kelayakan level tetap diterapkan
 * pada fallback itu — server merender tanpa tahu level pembeli, jadi daftarnya
 * selalu versi "tanpa level" yang harus disaring ulang di sini.
 */
export function useCampaignsForMe(fallback: PromoCampaign[] = []): PromoCampaign[] {
  const { campaigns, now, remaining } = useContext(PromoContext);
  const group = useCustomerGroup();
  const slot = group?.slot ?? null;
  return useMemo(() => {
    const source = now ? runningPromoCampaigns(campaigns, now) : fallback;
    return source.filter(
      (c) =>
        promoAllowsLevel({ levelSlots: c.levelSlots }, slot) &&
        // Kuota habis (mig 6020) = kampanye tak lagi ditawarkan. Disaring DI
        // SINI juga, bukan hanya di `usePromosFor`: rail & halaman `/promo`
        // menilai kampanye lewat hook ini, dan kampanye yang lolos ke daftar
        // sementara harganya sudah normal adalah persis kelas bug yang
        // melahirkan hook ini (lihat catatan di atas).
        promoHasRemaining(c.id, remaining)
    );
  }, [campaigns, now, fallback, slot, remaining]);
}

/**
 * Jam dinding bersama seluruh pohon storefront — di-seed `serverNow` lalu
 * berdetak tiap menit. Dipakai permukaan yang menilai periode kampanye SENDIRI
 * (halaman `/promo`, rail Promo beranda) supaya mereka memakai jam yang SAMA
 * dengan yang menghitung harga di kartu; dua jam berbeda dalam satu layar bisa
 * menampilkan produk di daftar promo sementara harganya sudah kembali normal.
 *
 * `null` hanya bila layout lupa mengirim `serverNow` — pemanggil sebaiknya
 * jatuh ke daftar yang sudah diresolusi server, bukan menebak jamnya sendiri
 * (menebak = risiko hydration mismatch React #418).
 */
export function usePromoNow(): Date | null {
  return useContext(PromoContext).now;
}

/**
 * Sisa kuota kampanye (mig 6020) — untuk chip "Sisa N pcs" di kartu, halaman
 * produk, dan `/promo`. Kunci absen / bernilai null = tak berkuota atau belum
 * terbaca; permukaan yang membacanya TIDAK menampilkan chip apa pun, bukan
 * menampilkan "0".
 */
export function usePromoRemaining(): PromoRemainingMap {
  return useContext(PromoContext).remaining;
}

/**
 * Promo yang berlaku untuk satu produk — bentuk yang langsung dioper ke
 * `getVariantPrice` / `getProductPrice`.
 *
 * HIDRASI. Jam yang dipakai berasal dari `serverNow`, jadi HTML server dan
 * render klien pertama menghasilkan harga yang IDENTIK — tak ada harga normal
 * yang berkedip jadi harga promo setelah mount, dan tak ada hydration mismatch
 * (#418). `now === null` hanya terjadi bila layout lupa mengirim `serverNow`;
 * dalam keadaan itu kita memulangkan kosong (harga normal) alih-alih menebak.
 */
export function usePromosFor(product: PromoProductRef | null | undefined): ProductPromo[] {
  const { campaigns, now, remaining } = useContext(PromoContext);
  const group = useCustomerGroup();
  const slot = group?.slot ?? null;
  return useMemo(() => {
    if (!product || !now || campaigns.length === 0) return [];
    // KELAYAKAN LEVEL DISARING DI SINI, bukan hanya di lapisan harga (mig 3680).
    // Alasannya ada di pemakainya: `HomeProductCard` & `ProductDetail` memakai
    // `promos.length` untuk memasang pil "🔥" dan MENYEMBUNYIKAN badge
    // "Harga <Grup>". Kalau daftar ini masih memuat kampanye yang tak berhak
    // bagi pembeli ini, kartunya memasang 🔥 pada harga yang sama sekali tidak
    // didiskon — pembeli membaca promo yang tak ada, dan badge harga levelnya
    // ikut hilang. Aturan kelayakannya SATU (`promosForLevel`), dipakai bersama
    // `pricing.ts`, jadi angka & tampilan mustahil menyimpang.
    // `remaining` (mig 6020): kampanye yang kuotanya HABIS tak ikut menghitung
    // harga — kalau ia tetap ikut, pembeli melihat harga promo yang pasti
    // ditolak trigger saat checkout, dan kegagalannya baru terbaca di langkah
    // terakhir. Sisa yang belum/gagal terbaca tak pernah membuang kampanye.
    return promosForLevel(promosForProduct(campaigns, product, now, { remaining }), slot);
  }, [campaigns, now, product, slot, remaining]);
}

/**
 * Promo yang berlaku bagi TAMU untuk satu produk, APA PUN level pembeli yang
 * sedang login — dasar harga yang dikirim pixel iklan (B7, PR-IG-4: harga level
 * reseller/agen rahasia toko). Tidak bisa diturunkan dari `usePromosFor`:
 * daftar itu sudah disaring level SESI, jadi bagi reseller kampanye umum yang
 * tak mencakup levelnya sudah hilang. Rumusnya `guestPromosFor` — sama dengan
 * harga tamu feed Katalog Instagram & JSON-LD. Jam & sisa kuota dari konteks
 * yang sama dengan `usePromosFor` (tanpa hydration mismatch).
 */
export function useGuestPromosFor(product: PromoProductRef | null | undefined): ProductPromo[] {
  const { campaigns, now, remaining } = useContext(PromoContext);
  return useMemo(() => {
    if (!product || !now || campaigns.length === 0) return [];
    return guestPromosFor(campaigns, product, now, { remaining });
  }, [campaigns, now, product, remaining]);
}
